/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { safeParse } from '@src/utils/string.util'

import { CACHE, MIDDLEWARE_TIMEOUTS, ONE_MONTH_IN_SECONDS } from './utils/constants.util'

const vercelUrl = `https://${process.env.VERCEL_URL || 'www.beachbody.com'}`
const siteMapUrl = process.env.SITEMAP_URL_OVERRIDE || vercelUrl

/**
 * PUBLIC config values that may be exposed to the client.
 */
export interface ConfigPublic {
  general: {
    isProduction: boolean
    vercelEnv: string
    isDeployed: () => boolean
    isCacheEnabled: () => boolean
    appVersion: string
    commitSHA: string
    test?: string
    beachbodyUrl: string
    bodUrl: string
    bodIosAppUrl: string
    bodAndroidAppUrl: string
    tbbUrl: string
    eligibilityRedirectUrl: string
    siteMapUrl: string
    supportedLocales: string[]
    cookieDomain: string
    isCartIconOnCheckout: boolean
    topNavDelay: number
    middlewareTimeouts: typeof MIDDLEWARE_TIMEOUTS
    inactivityTimeout: number
    webSourceCodesToDisableSetPassword: string[]
  }
  logs: {
    logsVisibleInConsole: boolean
    logLevelClient: string
    logLevelServer: string
    sensitiveFieldPatterns: string[]
    safeFieldPatterns: string[]
  }
  amplitude: {
    clientDeploymentKey: string
    apiKey: string
    logLevel: number
  }
  builder: {
    apiKey: string
    cacheMap: Record<string, string>
  }
  cybersource: {
    iframeUrl: string
  }
  datadog: {
    allowedTracingUrls: string[]
    applicationId: string
    clientToken: string
    env: string
    isEnabled: boolean
    sessionSampleRate: number
    sessionReplaySampleRate: number
    site: string
    service: string
  }
  featureFlags: {
    partnerEnabled: boolean
    guestCheckoutEnabled: boolean
    myAccountPageEnabled: boolean
    inspectUserPropertiesEnabled: boolean
    microformV2Enabled: boolean
    checkoutLoginEnabled: boolean
  }
  googleTagManager: {
    id: string
    auth?: string
    previewEnv?: string
  }
  googleRecaptcha: {
    siteKey: string
  }
  orderService: {
    apiUrl: string
    apiKey: string
    apiGatewayUrl: string
  }
  eventService: {
    apiUrl: string
    apiKey: string
    awsRegion: string
    awsService: string
  }
  osano: {
    configId: string
    configVersion: string
    region1CountryCodes: string[]
  }
  consentsService: {
    apiUrl: string
    apiKey: string
  }
  threatmetrix: {
    url: string
    orgId: string
  }
  affiliate: {
    cookieMaxAge: string
    webSourceCode: string
  }
  loudCrowd: {
    scriptUrl: string
    shopId: string
  }
  sse: {
    scriptUrl: string
    cssUrl: string
    isSplashed: boolean
  }
  applePay: {
    merchantId: string
    allowCardsFromAnyCountry: boolean
    isEnabled: boolean
    env: string
  }
  sierra: {
    isEnabled: boolean
    scriptUrl: string
  }
  socialLadder: {
    areaGuid: string
    isEnabled: boolean
    isConversionEnabled: boolean
    isSplashed: boolean
    cookieMaxAge: string
    shopCode?: {
      BodiAffiliates?: string
      BodiExperience?: string
    }
  }
}

export const configPublic: ConfigPublic = {
  general: {
    isProduction: process.env.NEXT_PUBLIC_VERCEL_ENV === 'production',
    vercelEnv: String(process.env.NEXT_PUBLIC_VERCEL_ENV),
    isDeployed: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    isCacheEnabled: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'development',
    commitSHA: (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? '').slice(0, 7),
    test: process.env.NEXT_PUBLIC_TEST,
    beachbodyUrl: process.env.NEXT_PUBLIC_BEACHBODY_URL ?? 'https://www.beachbody.com',
    bodUrl: process.env.NEXT_PUBLIC_BOD_URL ?? 'https://www.beachbodyondemand.com',
    bodIosAppUrl:
      process.env.NEXT_PUBLIC_BOD_IOS_APP_URL ??
      'https://apps.apple.com/us/app/beachbody-on-demand/id1031660123',
    bodAndroidAppUrl:
      process.env.NEXT_PUBLIC_BOD_ANDROID_APP_URL ??
      'https://play.google.com/store/apps/details?id=com.beachbody.bod',
    tbbUrl: process.env.NEXT_PUBLIC_TBB_URL ?? 'https://www.teambeachbody.com',
    eligibilityRedirectUrl: process.env.NEXT_PUBLIC_ELIGIBILITY_REDIRECT_URL ?? '/c/fitness',
    siteMapUrl,
    supportedLocales: safeParse<string[]>(process.env.NEXT_PUBLIC_SUPPORTED_LOCALES) || [
      'en-US',
      'en-CA',
      'fr-CA',
      'en-GB',
    ],
    cookieDomain: process.env.NODE_ENV === 'development' ? 'localhost' : '.bodi.com',
    isCartIconOnCheckout: process.env.NEXT_PUBLIC_IS_CART_ICON_ON_CHECKOUT === 'true',
    topNavDelay: safeParse<number>(process.env.NEXT_PUBLIC_TOPNAV_DELAY) ?? 200,
    middlewareTimeouts:
      safeParse<typeof MIDDLEWARE_TIMEOUTS>(process.env.NEXT_PUBLIC_MIDDLEWARE_TIMEOUTS) ||
      MIDDLEWARE_TIMEOUTS,
    inactivityTimeout: Number(safeParse<number>(process.env.NEXT_PUBLIC_INACTIVITY_TIMEOUT) || 0),
    webSourceCodesToDisableSetPassword:
      safeParse<string[]>(process.env.NEXT_PUBLIC_WEB_SOURCE_CODES_TO_DISABLE_SET_PASSWORD) || [],
  },
  logs: {
    logsVisibleInConsole: process.env.NEXT_PUBLIC_LOGS_VISIBLE_IN_CONSOLE === 'true',
    logLevelClient: process.env.NEXT_PUBLIC_LOG_LEVEL_CLIENT ?? 'debug',
    logLevelServer: process.env.NEXT_PUBLIC_LOG_LEVEL_SERVER ?? 'info',
    sensitiveFieldPatterns: safeParse<string[]>(
      process.env.NEXT_PUBLIC_SENSITIVE_FIELD_PATTERNS
    ) || [
      'password',
      'token',
      'secret',
      'flexkey',
      'auth',
      'session',
      'cookie',
      'csrf',
      'jwt',
      'bearer',
      'firstName',
      'lastName',
      'address',
      'streetName',
      'additionalStreetInfo',
      'city',
      'zip',
      'postalCode',
      'email',
      'cardExpiration',
      'cvc',
      'cvv',
      'signature',
    ],
    safeFieldPatterns: safeParse<string[]>(process.env.NEXT_PUBLIC_SAFE_FIELD_PATTERNS) || [
      '^CustomerEmail$',
      '^customerEmailDomain$',
      '^clickIdCookie$',
      '^payerAuthenticationTransaction$',
    ],
  },
  amplitude: {
    clientDeploymentKey: process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY ?? '',
    apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '',
    logLevel: Number(process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL ?? 0),
  },
  builder: {
    apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY ?? '',
    cacheMap: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_BUILDER_CACHE_MAP) ?? {
      a2c65c9f3dfa480d9c395a195ea5c0f5: CACHE.page,
      '96dd32ede8cf4d098b2b95ca0be5986f': CACHE.notFoundSection,
      '23f370c8a21b47ca834adc6ac287a1c5': CACHE.flowRouterRules,
      adc4dd6f890e4613915772a280b49ed6: CACHE.nutritionPDP,
      '6a29f9b7c39747c8ba4e0bfaf1bcc44e': CACHE.digitalPDP,
    },
  },
  cybersource: {
    iframeUrl: process.env.NEXT_PUBLIC_CYBERSOURCE_IFRAME_URL ?? '',
  },
  datadog: {
    allowedTracingUrls: safeParse(process.env.NEXT_PUBLIC_DATADOG_RUM_ALLOWED_TRACING_URLS) || [],
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    env: process.env.NEXT_PUBLIC_ENV ?? 'local',
    isEnabled: process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED
      ? process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED === 'true'
      : true,
    sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE ?? 100),
    sessionReplaySampleRate: Number(
      process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE_SESSION_REPLAY ?? 100
    ),
    site: 'datadoghq.com',
    service: 'commerce-web',
  },
  featureFlags: {
    partnerEnabled: process.env.NEXT_PUBLIC_PARTNER_ENABLED === 'true',
    guestCheckoutEnabled: process.env.NEXT_PUBLIC_GUEST_CHECKOUT_ENABLED === 'true',
    myAccountPageEnabled: process.env.NEXT_PUBLIC_MY_ACCOUNT_ENABLED === 'true',
    inspectUserPropertiesEnabled:
      process.env.NEXT_PUBLIC_INSPECT_USER_PROPERTIES_ENABLED === 'true',
    microformV2Enabled: process.env.NEXT_PUBLIC_MICROFORM_V2_ENABLED === 'true',
    checkoutLoginEnabled: process.env.NEXT_PUBLIC_CHECKOUT_LOGIN_ENABLED === 'true',
  },
  googleTagManager: {
    id: process.env.NEXT_PUBLIC_GTM_ID ?? 'GTM-NX2LQ8F',
    auth: process.env.NEXT_PUBLIC_GTM_AUTH,
    previewEnv: process.env.NEXT_PUBLIC_GTM_PREVIEW_ENV,
  },
  googleRecaptcha: {
    siteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '',
  },
  orderService: {
    apiUrl: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_KEY ?? '',
    apiGatewayUrl: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_GATEWAY_URL ?? '',
  },
  eventService: {
    apiUrl: process.env.NEXT_PUBLIC_EVENT_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_EVENT_SERVICE_API_KEY ?? '',
    awsRegion: process.env.NEXT_PUBLIC_EVENT_SERVICE_AWS_REGION ?? 'us-west-2',
    awsService: process.env.NEXT_PUBLIC_EVENT_SERVICE_AWS_SERVICE ?? 'execute-api',
  },
  osano: {
    configId: process.env.NEXT_PUBLIC_OSANO_ID ?? '',
    configVersion: process.env.NEXT_PUBLIC_OSANO_VERSION ?? 'v1.3.0',
    region1CountryCodes: (
      safeParse<string[]>(process.env.NEXT_PUBLIC_OSANO_REGION1_COUNTRY_CODES) ?? ['US', 'CA']
    ).map((c) => c.toUpperCase()),
  },
  consentsService: {
    apiUrl: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_KEY ?? '',
  },
  threatmetrix: {
    url: process.env.NEXT_PUBLIC_THREATMETRIX_URL ?? '',
    orgId: process.env.NEXT_PUBLIC_THREATMETRIX_ORG_ID ?? '',
  },
  affiliate: {
    cookieMaxAge: process.env.NEXT_PUBLIC_AFFILIATE_COOKIE_MAX_AGE ?? '10368000',
    webSourceCode: process.env.NEXT_PUBLIC_AFFILIATE_WEB_SOURCE_CODE ?? 'AFF_BODI_IMP',
  },
  loudCrowd: {
    scriptUrl: process.env.NEXT_PUBLIC_LOUDCROWD_SCRIPT_URL ?? 'https://pub.loudcrowd.com/embed.js',
    shopId: process.env.NEXT_PUBLIC_LOUDCROWD_SHOP_ID ?? 'aW50ZWdyYXRpb246ODU3',
  },
  sse: {
    scriptUrl:
      process.env.NEXT_PUBLIC_SSE_SCRIPT_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.js',
    cssUrl:
      process.env.NEXT_PUBLIC_SSE_CSS_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.css',
    isSplashed: process.env.NEXT_PUBLIC_SSE_IS_SPLASHED === 'true',
  },
  applePay: {
    merchantId: process.env.NEXT_PUBLIC_APPLE_MERCHANT_ID ?? 'merchant.com.bodi.non-prod',
    allowCardsFromAnyCountry:
      process.env.NEXT_PUBLIC_APPLE_PAY_ALLOW_CARDS_FROM_ANY_COUNTRY === 'true',
    isEnabled: process.env.NEXT_PUBLIC_APPLE_PAY_ENABLED === 'true',
    env: process.env.NEXT_PUBLIC_ENV ?? 'dev',
  },
  sierra: {
    isEnabled: process.env.NEXT_PUBLIC_SIERRA_ENABLED === 'true',
    scriptUrl:
      process.env.NEXT_PUBLIC_SIERRA_SCRIPT_URL ??
      'https://sierra.chat/agent/Ut1QkpUBlHLzwwIBlHL4Ah5wk-5cYf0rK_kNTk3SH5E/embed',
  },
  socialLadder: {
    areaGuid: process.env.NEXT_PUBLIC_SOCIAL_LADDER_AREA_GUID ?? 'test',
    isEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_ENABLED === 'true',
    isConversionEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_CONVERSION_IS_ENABLED === 'true',
    isSplashed: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_SPLASHED === 'true',
    cookieMaxAge: process.env.NEXT_PUBLIC_SOCIAL_LADDER_COOKIE_MAX_AGE ?? `${ONE_MONTH_IN_SECONDS}`,
    shopCode: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_SHOP_CODE_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
  },
}
